import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Users, BookOpen, Settings2, HeartHandshake, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page } from "@/components/shared";

const MODULES = [
  { id: "getting_ready", title: "Getting Ready", desc: "Pack smart, dress for the road, plan your day." },
  { id: "on_the_road", title: "On the Road", desc: "Stay aware, pace yourself, mind the crowd." },
  { id: "consent", title: "Consent & Boundaries", desc: "Respect, wine responsibly, read the room." },
  { id: "alcohol", title: "Responsible Celebration", desc: "Hydrate, know your limits, look after your crew." },
  { id: "staying_together", title: "Staying With Your People", desc: "Buddy up, set meeting points, check in often." },
  { id: "getting_home", title: "Getting Home", desc: "Plan your ride before you're tired." },
  { id: "something_happens", title: "If Something Happens", desc: "Know the help pathways and Safe Points." },
];

export default function Passport() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const p = user?.passport || {};
  const [ecName, setEcName] = useState(p.emergency_contact_name || "");
  const [ecPhone, setEcPhone] = useState(p.emergency_contact_phone || "");
  const [support, setSupport] = useState(p.support_info || "");
  const [done, setDone] = useState(p.modules_completed || []);
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setDone((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]));

  const progress = () => {
    let n = 0;
    if (ecName && ecPhone) n += 1;
    n += Math.min(done.length, 4) / 4 * 3;
    return Math.round((n / 4) * 100);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/passport", {
        emergency_contact_name: ecName, emergency_contact_phone: ecPhone,
        support_info: support, modules_completed: done,
      });
      await refresh();
      toast.success("Safety Passport saved!");
      nav("/app");
    } catch { toast.error("Could not save. Try again."); }
    finally { setSaving(false); }
  };

  const pct = progress();

  return (
    <Page className="px-5 pt-6">
      <button data-testid="passport-back" onClick={() => nav("/app")} className="cs-tap flex items-center gap-1 text-sm mb-4" style={{ color: "var(--cs-fg2)" }}>
        <ArrowLeft size={18} /> Home
      </button>

      <h1 className="font-head font-extrabold text-3xl">Safety Passport</h1>
      <p className="mt-2 text-base" style={{ color: "var(--cs-fg2)" }}>A safer Carnival starts with you. Let's get you ready — under 5 minutes.</p>

      <div className="mt-5">
        <div className="flex justify-between text-xs mb-1.5">
          <span className="font-semibold">Progress</span>
          <span style={{ color: "var(--cs-magenta)" }}>{pct}%</span>
        </div>
        <div className="rounded-full overflow-hidden" style={{ height: 8, background: "var(--cs-card)" }}>
          <div className="h-full magenta-grad" style={{ width: `${pct}%`, transition: "width .4s" }} />
        </div>
      </div>

      <Section icon={Phone} title="Emergency Contact">
        <input data-testid="passport-ec-name" value={ecName} onChange={(e) => setEcName(e.target.value)} placeholder="Contact name" className="pp-input" />
        <input data-testid="passport-ec-phone" value={ecPhone} onChange={(e) => setEcPhone(e.target.value)} placeholder="Phone number" className="pp-input mt-2" />
      </Section>

      <Section icon={Users} title="My Crew / Buddy Group">
        <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>{user?.crew_id ? "You're in a crew. Manage it from My Crew." : "Join or create a crew from the My Crew screen."}</p>
        <button data-testid="passport-goto-crew" onClick={() => nav("/app/crew")} className="cs-tap mt-2 text-sm font-semibold" style={{ color: "var(--cs-magenta)" }}>Go to My Crew →</button>
      </Section>

      <Section icon={BookOpen} title="Safety 101">
        <p className="text-sm mb-3" style={{ color: "var(--cs-fg2)" }}>Short, practical modules. Tap to mark complete (4+ recommended).</p>
        <div className="space-y-2">
          {MODULES.map((m) => {
            const on = done.includes(m.id);
            return (
              <button key={m.id} data-testid={`passport-module-${m.id}`} onClick={() => toggle(m.id)}
                className="cs-tap w-full flex items-center gap-3 rounded-xl p-3 text-left"
                style={{ background: on ? "rgba(16,185,129,0.12)" : "var(--cs-card-hi)", border: `1px solid ${on ? "var(--cs-green)" : "var(--cs-border)"}` }}>
                <span className="grid place-items-center rounded-full flex-shrink-0" style={{ width: 26, height: 26, background: on ? "var(--cs-green)" : "transparent", border: on ? "none" : "2px solid var(--cs-border)" }}>
                  {on && <Check size={16} className="text-white" />}
                </span>
                <div>
                  <p className="font-semibold text-sm">{m.title}</p>
                  <p className="text-xs" style={{ color: "var(--cs-fg2)" }}>{m.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </Section>

      <Section icon={Settings2} title="Road Day Preferences">
        <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>Set your default meeting point and sharing preferences from More → Privacy.</p>
      </Section>

      <Section icon={HeartHandshake} title="Support Information">
        <textarea data-testid="passport-support" value={support} onChange={(e) => setSupport(e.target.value)} rows={2}
          placeholder="Anything responders should know (allergies, needs)…" className="pp-input" style={{ resize: "none", paddingTop: 12 }} />
      </Section>

      <button data-testid="passport-complete-button" onClick={save} disabled={saving}
        className="cs-tap w-full mt-6 magenta-grad magenta-glow rounded-2xl text-white font-head font-bold text-lg" style={{ height: 58 }}>
        {saving ? "Saving…" : "COMPLETE MY PASSPORT"}
      </button>
      <p className="text-center text-xs mt-3 mb-4" style={{ color: "#52525b" }}>
        Completing your passport means you've done basic preparation — not that you're certified "safe."
      </p>

      <style>{`.pp-input{width:100%;height:50px;border-radius:.75rem;padding:0 1rem;background:var(--cs-card-hi);border:1px solid var(--cs-border);color:#fff;outline:none;font-size:16px}.pp-input:focus{border-color:var(--cs-magenta)}`}</style>
    </Page>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="cs-card mt-4 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={20} style={{ color: "var(--cs-magenta)" }} />
        <h2 className="font-head font-bold text-lg">{title}</h2>
      </div>
      {children}
    </div>
  );
}
